"use client";

import {
  AIInsightType,
  AIInsightTypeEnum,
  BreadcrumbItem,
  DocsHeaderProps,
} from "./types";
import { NewsRoutePaths, ROUTER_PATH } from "@config/router";
import { OspToast, ospThemes } from "@ospweb/sdk-ui";
import { copyText, formatISOToReadable } from "utils";

import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { DocsBreadcrumb } from "./DocsBreadcrumb";
import { DocsMarkdown } from "../DocsMarkdown";
import { DocsPriceChange } from "./DocsPriceChange";
import { I18nUtil } from "@utils/i18n";
import Image from "next/image";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import Translations from "@translate/components/AIInsight/DocsHeader";
import { buildLocale } from "@utils/buildLocale";
import { config } from "@config/config";

const I18n = I18nUtil.createScoped(Translations);

interface FuncButtonProps {
  text: string;
  iconName: string;
  onClick: () => void;
  deviceType?: DeviceType;
}

const FuncButton = ({
  text,
  iconName,
  onClick,
  deviceType,
}: FuncButtonProps) => {
  return (
    <div
      data-openreplay-label="aiinsight_docsheader_click"
      className={`flex flex-row button xs_button items-center gap-[6px] bg-background_white_overlay10 hover:bg-background_white_overlay20 rounded-1.5 ${deviceType !== "desktop" ? "flex-1" : ""}`}
      onClick={onClick}
    >
      <span className=" button_s text-content_primary">{text}</span>
      <OspIcons
        name={iconName}
        size={16}
        color={ospThemes.colors.content_primary}
      />
    </div>
  );
};

export const DocsHeader = ({
  title,
  category,
  tokens,
  author,
  analyst,
  locale,
  headImage,
  deviceType,
  newsThemeItem,
  summary,
  enTitle,
}: DocsHeaderProps) => {
  // const homeHref =
  //   category === AIInsightTypeEnum.CRYPTO
  //     ? ROUTER_PATH.HOME_CRYPTO
  //     : ROUTER_PATH.HOME_SROCK;
  const insightCategory = category ?? "";
  const localePathPrefix = buildLocale(locale);
  const pathOpts = { localePathPrefix } as const;

  const articleUrl = NewsRoutePaths.subTag(insightCategory, enTitle || "", {
    ...pathOpts,
    siteOrigin: process.env.NEXT_PUBLIC_SITE_UR,
  });
  const shareText = `Check out this article, it explains it simply:${articleUrl}`;

  const authorLogo = analyst?.avatar?.trim() || author.logo;
  const authorName = analyst?.name?.trim() || author.name;
  const authorSlug = analyst?.slug?.trim();
  const authorProfileHref = authorSlug
    ? `${config.externalLink?.aiChat || ""}${locale !== "en" ? "/" + locale : ""}${ROUTER_PATH.NEWS_AUTHOR_DETAIL}/${encodeURIComponent(authorSlug)}`
    : undefined;

  const parentTheme = newsThemeItem?.parentTheme;

  const newsChannelSecondCrumb = (): BreadcrumbItem => {
    if (parentTheme?.slug && parentTheme.theme) {
      return {
        label: parentTheme.theme,
        href: NewsRoutePaths.hub(parentTheme.slug, pathOpts),
      };
    }
    if (newsThemeItem?.slug && newsThemeItem.theme) {
      return {
        label: newsThemeItem.theme,
        href: NewsRoutePaths.hub(newsThemeItem.slug, pathOpts),
      };
    }
    return {
      label: I18n.t("Themes"),
      href: undefined,
    };
  };

  const subTagNewsHref =
    parentTheme?.slug && newsThemeItem?.slug
      ? NewsRoutePaths.subTag(parentTheme.slug, newsThemeItem.slug, pathOpts)
      : undefined;

  const docsBreadcrumbItems: BreadcrumbItem[] = [
    {
      label:
        insightCategory === AIInsightTypeEnum.CRYPTO
          ? I18n.t("Crypto News")
          : I18n.t("Stock News"),
      href: undefined,
    },
    newsChannelSecondCrumb(),
    { label: title || "" }, // 当前页面
  ];

  const funcButtons: FuncButtonProps[] = [
    {
      text: I18n.t("Share to"),
      iconName: "X-logo",
      onClick: () => {
        const shareUrl = `https://x.com/compose/post?text=${shareText}`;
        window.open(shareUrl, "_blank");
      },
    },
    {
      text: I18n.t("Share to"),
      iconName: "Telegram",
      onClick: () => {
        const shareUrl = `https://t.me/share/url?url=${shareText}`;
        window.open(shareUrl, "_blank");
      },
    },
    {
      text: I18n.t("Copy link"),
      iconName: "link",
      onClick: () => {
        copyText(articleUrl);
        OspToast.success({
          content: I18n.t("Link copied successfully"),
        });
      },
    },
  ];

  const authorAvatarLabel = (
    <>
      <Image
        className="mr-1 rounded-full object-cover"
        src={authorLogo}
        alt={authorName}
        width={16}
        height={16}
      />
      <h2
        aria-label="Docs Header h2"
        data-openreplay-label="aiinsight_docsheader_h2"
        id="sem-docs-header-h2"
      >
        {authorName}
      </h2>
    </>
  );

  return (
    <header
      aria-label="Docs Header header"
      data-openreplay-label="aiinsight_docsheader_header"
      id="sem-docs-header-header"
      className="w-full h-auto flex flex-col"
    >
      <DocsBreadcrumb
        items={docsBreadcrumbItems}
        showHomeIcon={false}
        className={`sticky top-[52px] md:top-0 bg-background_solid_secondary z-80 h-[36px]`}
      />
      {subTagNewsHref && newsThemeItem?.theme ? (
        <ShellLink
          aria-label="Docs Header"
          data-openreplay-label="aiinsight_docsheader_open"
          id="lnk-docs-header"
          href={subTagNewsHref}
          className={`body_medium_s text-content_brand_primary cursor-pointer ${deviceType !== "desktop" ? "mt-0" : "mt-[14px]"}`}
        >
          {newsThemeItem.theme}
        </ShellLink>
      ) : undefined}
      <h1
        aria-label="Docs Header h1"
        data-openreplay-label="aiinsight_docsheader_h1"
        id={title}
        className="headline_regular_l text-content_primary mt-3"
      >
        {title}
      </h1>
      <div className={`flex mt-3 flex-col gap-3`}>
        <div className="flex flex-row items-center body_s text-content_tertiary gap-2">
          {authorProfileHref ? (
            <ShellLink
              aria-label="Docs Header"
              data-openreplay-label="aiinsight_docsheader_open"
              id="lnk-docs-header-2"
              href={authorProfileHref}
              target="_blank"
              rel="author"
              className="flex flex-row items-center gap-2 text-content_tertiary hover:text-content_secondary transition-colors"
            >
              {authorAvatarLabel}
            </ShellLink>
          ) : (
            authorAvatarLabel
          )}
          <span>{`·`}</span>
          <span>{formatISOToReadable(author.time)}</span>
        </div>
        <div className="flex flex-row items-center gap-2">
          {funcButtons.map((button) => (
            <FuncButton key={button.text} {...button} deviceType={deviceType} />
          ))}
        </div>
      </div>
      {tokens && tokens.length > 0 && (
        <DocsPriceChange
          data={tokens}
          locale={locale}
          category={insightCategory as AIInsightType}
          deviceType={deviceType}
        />
      )}
      {summary && <DocsMarkdown content={summary} className="mt-8" />}
      <Image
        className="w-full object-cover mt-8"
        src={headImage}
        alt={title}
        width={deviceType !== "desktop" ? 630 : 361}
        height={deviceType !== "desktop" ? 360 : 203}
      />
    </header>
  );
};
